import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SearchableSelect.module.css';

export type SearchableSelectOption = {
  value: string;
  label: string;
};

export const optionsFromNames = (
  items: Array<{ id: string; name: string }>,
  currentName?: string
): SearchableSelectOption[] => {
  const opts = [...items]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((item) => ({ value: item.name, label: item.name }));
  const name = (currentName || '').trim();
  if (name && !opts.some((option) => option.value === name)) {
    opts.unshift({ value: name, label: name });
  }
  return opts;
};

type SearchableSelectProps = {
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  ariaLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  emptyOption?: SearchableSelectOption;
  placeholder?: string;
  className?: string;
  compact?: boolean;
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  options,
  onChange,
  ariaLabel,
  disabled = false,
  invalid = false,
  emptyOption,
  placeholder = '',
  className = '',
  compact = false,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const selectedLabel =
    value === '' && emptyOption
      ? emptyOption.label
      : options.find((option) => option.value === value)?.label || value || '';

  const filteredOptions = useMemo(() => {
    const q = (isTyping ? query : '').trim().toLowerCase();
    const matches = options.filter((option) => !q || option.label.toLowerCase().includes(q));
    if (!emptyOption) return matches;
    const emptyMatches = !q || emptyOption.label.toLowerCase().includes(q);
    return emptyMatches ? [emptyOption, ...matches] : matches;
  }, [options, query, isTyping, emptyOption]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setIsTyping(false);
      return undefined;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, open]);

  const selectOption = (option: SearchableSelectOption) => {
    onChange(option.value);
    setOpen(false);
  };

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setIsTyping(false);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  };

  return (
    <div
      className={`${styles.wrap} ${compact ? styles.compact : ''} ${open ? styles.open : ''} ${disabled ? styles.disabled : ''} ${className}`.trim()}
      data-searchable-select=""
      ref={wrapRef}
    >
      <input
        ref={inputRef}
        type="text"
        className={`${styles.input} ${invalid ? styles.invalid : ''}`.trim()}
        value={open && isTyping ? query : selectedLabel}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        onFocus={() => {
          if (!disabled) openList();
        }}
        onChange={(event) => {
          setIsTyping(true);
          setQuery(event.target.value);
          if (!open) setOpen(true);
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
            return;
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!open) {
              openList();
              return;
            }
            if (filteredOptions.length === 0) return;
            setHighlightedIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
            return;
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (filteredOptions.length === 0) return;
            setHighlightedIndex((index) => Math.max(index - 1, 0));
            return;
          }
          if (event.key === 'Enter' && open) {
            event.preventDefault();
            const option = filteredOptions[highlightedIndex];
            if (option) selectOption(option);
          }
        }}
      />
      <button
        type="button"
        className={styles.arrow}
        tabIndex={-1}
        disabled={disabled}
        aria-label="פתח רשימה"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          if (open) {
            setOpen(false);
          } else {
            openList();
          }
        }}
      />
      {open && !disabled && (
        <div className={styles.list} role="listbox" aria-label={ariaLabel}>
          {filteredOptions.length === 0 ? (
            <div className={styles.empty}>אין תוצאות</div>
          ) : (
            filteredOptions.map((option, index) => (
              <button
                key={`${option.value}-${option.label}`}
                type="button"
                className={`${styles.option} ${option.value === value ? styles.optionActive : ''} ${index === highlightedIndex ? styles.optionHighlighted : ''}`.trim()}
                role="option"
                aria-selected={option.value === value}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectOption(option);
                }}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
